import React, { useEffect, useState, useCallback } from "react";
import { Heart, Home as HomeIcon, Briefcase, Users, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { t } from "@/i18n";

const META = {
  family:  { icon: HomeIcon,       color: "#5C7A5A", labelKey: "context_family"  },
  work:    { icon: Briefcase,      color: "#C97B4C", labelKey: "context_work"    },
  love:    { icon: Heart,          color: "#D64F6E", labelKey: "context_love"    },
  friends: { icon: Users,          color: "#8B9E88", labelKey: "context_friends" },
  school:  { icon: GraduationCap,  color: "#7A6B58", labelKey: "context_school"  },
};

function Ring({ percent, size = 84, stroke = 8, color = "#5C7A5A" }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size/2} cy={size/2} r={r} stroke="currentColor" strokeOpacity="0.15"
              strokeWidth={stroke} fill="none" />
      <circle cx={size/2} cy={size/2} r={r} stroke={color}
              strokeWidth={stroke} strokeLinecap="round" fill="none"
              strokeDasharray={`${dash} ${c}`}
              style={{ transition: "stroke-dasharray 0.6s ease" }} />
    </svg>
  );
}

export default function ProgressRings() {
  const { lang } = useLang();
  const [rings, setRings] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/practice/by-context");
      setRings(data.rings || []);
    } catch (e) {
      console.warn("progress rings fetch failed", e?.message || e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!rings.length) return null;

  return (
    <div className="p-6 rounded-2xl border border-border bg-card" data-testid="progress-rings">
      <div className="text-xs uppercase tracking-widest text-accent font-medium mb-4">
        {t(lang, "progress_rings")}
      </div>
      <div className="grid grid-cols-5 gap-4 sm:gap-6">
        {rings.map((r) => {
          const m = META[r.context] || {};
          const Icon = m.icon || Heart;
          return (
            <div key={r.context} className="flex flex-col items-center gap-2" data-testid={`ring-${r.context}`}>
              <div className="relative">
                <Ring percent={r.percent} color={m.color} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: m.color }} />
                </div>
              </div>
              <div className="text-xs font-medium text-center">{t(lang, m.labelKey)}</div>
              <div className="text-[10px] text-muted-foreground font-mono" data-testid={`ring-${r.context}-pct`}>
                {r.percent}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
